<script setup>
import { ref, onMounted, nextTick, inject } from 'vue'
import * as yup from 'yup'
import Papa from 'papaparse'
import { RequestService } from '../shared/services/RequestService'
import { toString } from 'lodash'
import { calculateGroupInsuranceTypeId } from '../shared/utils'
import { useSystemParamsStore } from '../shared/stores/SystemParamsStore'
import bootstrap from 'bootstrap/dist/js/bootstrap.bundle'
import { getExcludedDomains, isExcludedEmail } from '../shared/utils/excludedDomains'
import { splitOwnerCell } from '../shared/utils/accountOwnersHelpers'

const csvFile = ref(null)
const loading = ref(false)
const processedEmployers = ref([])
const errors = ref([])
const agencies = ref([])
const selectedAgency = ref(null)
const showCsvHints = ref(false)
const fileInputRef = ref(null)

const { post, simpleGet, handleErrors } = RequestService()
const functionsBaseUrl = inject('functionsBaseUrl')

onMounted(async () => {
  try {
    const response = await simpleGet(
      'loadAgencies',
      `${functionsBaseUrl}/agenciesGateway/api/v1/agencies`
    )
    agencies.value = response?.data?.data || []
  } catch (err) {
    handleErrors('loadAgencies', err)
  }
  // initialize Bootstrap tooltips
  nextTick(() => {
    const triggers = document.querySelectorAll('[data-bs-toggle="tooltip"]')
    triggers.forEach(el => new bootstrap.Tooltip(el))
  })
})

// Determine if the minimal set of plan questions is complete for a CSV row
// Required IDs: 7 (Control Group), 8 (ERISA Plan), 9 (Employees on Plan),
// 11 (Funding Type), 12 (Coverage multi-select)
function isPlanCompleteRow(answersArray) {
  const requiredIds = [7, 8, 9, 11, 12]
  return requiredIds.every((qid) => {
    const found = answersArray.find(a => a.id === qid)
    if (!found) return false
    const val = found.answer
    return !(val === '' || val === null || val === undefined)
  })
}

// Function to download CSV template
const downloadTemplate = () => {
  const csvContent = [
    'Account Owner,Related Account/Employee Name,EIN,Plan Year Start Date,Primary Contact Name,Primary Contact Email 1,Primary Contact Email 2,Control Group Y/N,ERISA Plan Y/N,Employees on Plan,Funding Type,Group Medical? Y/N,Dental? Y/N,Vision? Y/N,Voluntary Supplemental Life Y/N,Voluntary Supplemental Life for Spouse/Dependents Y/N?,STD? Y/N,LTD? Y/N,FSA? Y/N,HRA? Y/N,ICHRA? Y/N,Wellness Program? Y/N,Tobacco Surcharge? Y/N,Worksite Benefits? Y/N,MEC Plan? Y/N,Dependent Care FSA? Y/N,HSA? Y/N,Employer-paid Group Term Life? Y/N',
    '"owner@agency.com","Test Name",123456789,1/1/2025,Test Name,test@email.com,test2@email.com,,No,4,Fully Insured,No,No,No,,,,,,,N,N,No,,N,N,N,N'
  ].join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.setAttribute('download', 'employers_template.csv')
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const processCSV = file => {
  return new Promise(resolve => {
    Papa.parse(file, {
      header: true,
      complete: results => {
        const validRows = []
        const invalidRows = []
        // Ignore trailing empty rows: compute last index of non-empty data row
        const data = results.data
        const lastNonEmptyIdx = data.reduce((last, r, i) => {
          const emptyRow = Object.values(r).every(val => !val || String(val).trim() === '')
          return !emptyRow ? i : last
        }, -1)
        // Trim trailing empty rows
        const trimmedData = data.slice(0, lastNonEmptyIdx + 1)
        trimmedData.forEach((row, idx) => {
          const rowNumber = idx + 2
          // Check for fully empty line
          const isEmptyRow = Object.values(row).every(val => !val || String(val).trim() === '')
          if (isEmptyRow) {
            // Only mark blank within trimmed rows as error
            invalidRows.push({ row, rowNumber, error: 'Empty row' })
            return
          }
          const rowErrors = []
          // Validate Account Owner (optional; up to 3 valid emails separated by ';' or ',',
          // each optionally wrapped in quotes)
          const ownerCell = row['Account Owner'] ? String(row['Account Owner']).trim() : ''
          row['Account Owner'] = ownerCell
          if (ownerCell) {
            const ownerEmails = splitOwnerCell(ownerCell)
            if (ownerEmails.length > 3) {
              rowErrors.push('Account Owner allows a maximum of 3 emails')
            } else if (!ownerEmails.every(e => yup.string().email().isValidSync(e))) {
              rowErrors.push('Account Owner must contain valid email addresses')
            }
          }
          // Validate required name
          if (!row['Related Account/Employee Name'] || !String(row['Related Account/Employee Name']).trim()) {
            rowErrors.push('Related Account/Employee Name is required')
          }
          // Validate and format date
          const dateStr = row['Plan Year Start Date'] ? String(row['Plan Year Start Date']).trim() : ''
          if (!dateStr) {
            rowErrors.push('Plan Year Start Date is required')
          } else {
            const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
            if (match) {
              const mm = match[1].padStart(2, '0')
              const dd = match[2].padStart(2, '0')
              const y = match[3]
              row['Plan Year Start Date'] = `${mm}/${dd}/${y}`
            } else {
              rowErrors.push('Plan Year Start Date must be in mm/dd/yyyy format')
            }
          }
          // Validate Employees on Plan number if present
          const empCountStr = row['Employees on Plan'] ? String(row['Employees on Plan']).trim() : ''
          if (empCountStr) {
            if (/^\d+$/.test(empCountStr)) {
              row['Employees on Plan'] = Number(empCountStr)
            } else {
              rowErrors.push('Employees on Plan must be a valid number')
            }
          }
          // Validate boolean fields: Yes/No must be valid if present
          Object.keys(row).forEach(field => {
            const keyLower = field.toLowerCase()
            if (keyLower.includes('y/n') || keyLower.includes('yes/no')) {
              const raw = row[field] ? String(row[field]).trim() : ''
              if (!raw) {
                // no response, remove field
                delete row[field]
              } else {
                const low = raw.toLowerCase()
                if (low === 'yes' || low === 'y') {
                  row[field] = true
                } else if (low === 'no' || low === 'n') {
                  row[field] = false
                } else {
                  rowErrors.push(`${field} must be Yes or No`)
                }
              }
            }
          })
          // Validate Funding Type: raise error if invalid and set numeric value
          const fundingStr = row['Funding Type'] ? String(row['Funding Type']).trim() : ''
          if (fundingStr) {
            const fundingOptions = [
              'Fully Insured',
              'Self-Insured',
              'Level Funded',
              "Don't Know"
            ]
            const idxFund = fundingOptions.findIndex(opt => opt.toLowerCase() === fundingStr.toLowerCase())
            if (idxFund < 0) {
              rowErrors.push(`Funding Type must be one of: ${fundingOptions.join(', ')}`)
            } else {
              row['Funding Type'] = idxFund
            }
          }
          if (rowErrors.length > 0) {
            invalidRows.push({ row, rowNumber, error: rowErrors.join(' - ') })
          } else {
            // Build answers array with question IDs and parsed values
            const answers = []
            let _noEmployees = null
            let _fundingType = null
            // Multi-select question 12: collect indices of true selections
            const multiSelectMap = {
              'Group Medical? Y/N': 0,
              'Dental? Y/N': 1,
              'Vision? Y/N': 2,
              'Employer-paid Group Term Life? Y/N': 3,
              'Voluntary Supplemental Life Y/N': 4,
              'Voluntary Supplemental Life for Spouse/Dependents Y/N?': 5,
              'STD? Y/N': 6,
              'LTD? Y/N': 7,
              'FSA? Y/N': 8,
              'HRA? Y/N': 9,
              'ICHRA? Y/N': 10,
              'Wellness Program? Y/N': 11,
              'Tobacco Surcharge? Y/N': 12,
              'Worksite Benefits? Y/N': 13,
              'MEC Plan? Y/N': 14,
              'Dependent Care FSA? Y/N': 15,
              'HSA? Y/N': 16
            }
            const msSelections = []
            Object.entries(multiSelectMap).forEach(([col, idx]) => {
              if (row[col] === true) msSelections.push(idx)
              delete row[col]
            })
            if (msSelections.length > 0) {
              answers.push({ id: 12, answer: JSON.stringify(msSelections) })
            }
            // Single-answer questions
            const questionMap = {
              'Control Group Y/N': 7,
              'ERISA Plan Y/N': 8,
              'Employees on Plan': 9,
              'Funding Type': 11
            }
            Object.entries(questionMap).forEach(([col, qId]) => {
              if (row[col] !== undefined) {
                const val = row[col]
                // API convention for q7 (Control Group) and q8 (ERISA Plan): 0 = Yes, 1 = No
                const answer = (qId === 7 || qId === 8) ? (val ? 0 : 1) : val
                answers.push({ id: qId, answer: toString(answer) })
                if (qId === 9) _noEmployees = Number(val)
                if (qId === 11) _fundingType = Number(val)
                delete row[col]
              }
            })
            // Calculate and attach group insurance type only if plan is complete for this row
            const { largeGroupCutoff } = useSystemParamsStore()
            const giType = isPlanCompleteRow(answers)
              ? calculateGroupInsuranceTypeId(_noEmployees, _fundingType, largeGroupCutoff)
              : null
            row.groupInsuranceType = giType
            // Attach answers array
            row.answers = answers
            validRows.push(row)
          }
        })
        resolve({ validRows, invalidRows })
      }
    })
  })
}

const handleFileUpload = event => {
  const file = event.target.files[0]
  if (file) csvFile.value = file
}

const processEmployers = async () => {
  if (!csvFile.value) {
    handleErrors('processEmployers', new Error('Please select a CSV file'))
    return
  }
  if (!selectedAgency.value) {
    handleErrors('processEmployers', new Error('Please select an agency'))
    return
  }
  loading.value = true
  processedEmployers.value = []
  errors.value = []

  try {
    const { validRows, invalidRows } = await processCSV(csvFile.value)

    // Display invalid rows in UI
    invalidRows.forEach(({ rowNumber, error }) => {
      errors.value.push({ id: rowNumber, error })
    })

    if (invalidRows.length > 0) {
      // abort sending if any row invalid
      return
    }
    // Validate Account Owner emails exist and belong to the selected agency.
    // Querying agency members avoids case-sensitive misses from legacy mixed-case emails.
    const emailRowMap = new Map()
    validRows.forEach((r, i) => {
      const cell = r['Account Owner'] ? String(r['Account Owner']).trim() : ''
      if (!cell) return
      splitOwnerCell(cell).map(e => e.toLowerCase()).forEach(email => {
        if (!emailRowMap.has(email)) emailRowMap.set(email, [])
        emailRowMap.get(email).push(i + 1)
      })
    })
    const ownerEmails = [...emailRowMap.keys()]
    const validOwners = new Set()
    const excludedDomains = await getExcludedDomains()
    const membersResponse = await simpleGet(
      'loadAgencyMembers',
      `${functionsBaseUrl}/agenciesGateway/api/v1/agencies/${encodeURIComponent(selectedAgency.value)}/members?includePending=true`
    )
    ;(membersResponse?.data?.data || []).forEach(member => {
      const normalizedEmail = String(member.email || '').trim().toLowerCase()
      if (normalizedEmail && emailRowMap.has(normalizedEmail)) {
        validOwners.add(normalizedEmail)
      }
    })
    ownerEmails.forEach(email => {
      const rows = emailRowMap.get(email)
      const rowLabel = rows.length === 1 ? `row ${rows[0]}` : `rows ${rows.join(', ')}`
      if (isExcludedEmail(email, excludedDomains)) {
        errors.value.push({
          id: 'Account Owner',
          error: `'${email}' belongs to an excluded domain and cannot be assigned as Account Owner (${rowLabel})`
        })
      } else if (!validOwners.has(email)) {
        errors.value.push({
          id: 'Account Owner',
          error: `'${email}' does not exist or does not belong to the selected agency (${rowLabel})`
        })
      }
    })
    if (errors.value.length > 0) return
    // send all valid rows since no errors
    const response = await post(
      'loadEmployers',
      `${functionsBaseUrl}/employerGateway/api/v1/employers/import`,
      { agency_id: selectedAgency.value, records: validRows }
    )
    // API returns created items in response.data.data.created
    if (response?.data?.data?.created) {
      response.data.data.created.forEach(item => {
        processedEmployers.value.push({ id: item.employer.id, status: `${item.employer.name} - Created` })
      })
    } else {
      processedEmployers.value.push({ id: 'batch', status: `Processed ${validRows.length}` })
    }
  } catch (error) {
    handleErrors('processEmployers', error)
    errors.value.push({ id: 'System', error: 'Error processing CSV' })
  } finally {
    loading.value = false
    csvFile.value = null
    if (fileInputRef.value) fileInputRef.value.value = ''
  }
}
</script>

<template>
  <div class="main-container">
    <div class="row d-flex justify-content-center">
      <div class="col-12 text-center my-3">
        <h1 class="mt-1 text-primary">Load Employers</h1>
      </div>
    </div>

    <div class="row d-flex justify-content-center">
      <div class="col-md-8">
        <div class="card">
          <div class="card-header bg-light">
            <h5 class="card-title header-20 text-secondary"><strong>Bulk Employer Upload</strong></h5>
          </div>
          <div class="card-body">
            <div class="mb-4">
              <p>Upload a CSV file with employer data</p>
              <p class="text-muted">The first row should contain the column names.</p>
            </div>

            <div class="mb-2">
              <button class="btn btn-outline-secondary" @click="downloadTemplate">Download CSV Template</button>
            </div>

            <div class="mb-4">
              <div class="card border mb-4">
                <div class="card-header bg-light" @click="showCsvHints = !showCsvHints" style="cursor: pointer;">
                  <h6 class="mb-0 text-body fw-semibold">
                    <span class="me-2">{{ showCsvHints ? '▼' : '▶' }}</span> CSV Columns & Hints
                  </h6>
                </div>
                <div v-show="showCsvHints" class="card-body p-2">
                  <ul class="list-unstyled mb-0">
                    <li class="d-flex justify-content-between align-items-center py-1">
                      <code class="text-body fw-semibold">Account Owner</code>
                      <span class="text-primary" data-bs-toggle="tooltip" title="Optional - Email of a user belonging to the selected agency. Leave empty for Unassigned" style="cursor: pointer; font-size:1.2em">ℹ️</span>
                    </li>
                    <li class="d-flex justify-content-between align-items-center py-1">
                      <code class="text-body fw-semibold">Related Account/Employee Name</code>
                      <span class="text-primary" data-bs-toggle="tooltip" title="Required - cannot be empty" style="cursor: pointer; font-size:1.2em">ℹ️</span>
                    </li>
                    <li class="d-flex justify-content-between align-items-center py-1">
                      <code class="text-body fw-semibold">EIN</code>
                      <span class="text-primary" data-bs-toggle="tooltip" title="9-digit Employer Identification Number" style="cursor: pointer; font-size:1.2em">ℹ️</span>
                    </li>
                    <li class="d-flex justify-content-between align-items-center py-1">
                      <code class="text-body fw-semibold">Plan Year Start Date</code>
                      <span class="text-primary" data-bs-toggle="tooltip" title="Required - Format mm/dd/yyyy" style="cursor: pointer; font-size:1.2em">ℹ️</span>
                    </li>
                    <li class="d-flex justify-content-between align-items-center py-1">
                      <code class="text-body fw-semibold">Control Group Y/N</code>
                      <span class="text-primary" data-bs-toggle="tooltip" title="Optional - Yes or No only" style="cursor: pointer; font-size:1.2em">ℹ️</span>
                    </li>
                    <li class="d-flex justify-content-between align-items-center py-1">
                      <code class="text-body fw-semibold">ERISA Plan Y/N</code>
                      <span class="text-primary" data-bs-toggle="tooltip" title="Optional - Yes or No only" style="cursor: pointer; font-size:1.2em">ℹ️</span>
                    </li>
                    <li class="d-flex justify-content-between align-items-center py-1">
                      <code class="text-body fw-semibold">Employees on Plan</code>
                      <span class="text-primary" data-bs-toggle="tooltip" title="Optional numeric value" style="cursor: pointer; font-size:1.2em">ℹ️</span>
                    </li>
                    <li class="d-flex justify-content-between align-items-center py-1">
                      <code class="text-body fw-semibold">Funding Type</code>
                      <span class="text-primary" data-bs-html="true" data-bs-toggle="tooltip" title="<ul class='mb-0'><li>Fully Insured</li><li>Self-Insured</li><li>Level Funded</li><li>Don't Know</li></ul>" style="cursor: pointer; font-size:1.2em">ℹ️</span>
                    </li>
                    <li class="d-flex justify-content-between align-items-center py-1">
                      <code class="text-body fw-semibold">Coverage Options (Multi-select Y/N)</code>
                      <span class="text-primary" data-bs-html="true" data-bs-toggle="tooltip" title="<ul class='mb-0'><li>Group Medical? Y/N</li><li>Dental? Y/N</li><li>Vision? Y/N</li><li>Voluntary Supplemental Life Y/N</li><li>Voluntary Supplemental Life for Spouse/Dependents Y/N?</li><li>STD? Y/N</li><li>LTD? Y/N</li><li>FSA? Y/N</li><li>HRA? Y/N</li><li>ICHRA? Y/N</li><li>Wellness Program? Y/N</li><li>Tobacco Surcharge? Y/N</li><li>Worksite Benefits? Y/N</li><li>MEC Plan? Y/N</li><li>Dependent Care FSA? Y/N</li><li>HSA? Y/N</li><li>Employer-paid Group Term Life? Y/N</li></ul>" style="cursor: pointer; font-size:1.2em">ℹ️</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="mb-4 row gx-2 align-items-end">
              <div class="col">
                <input
                  ref="fileInputRef"
                  type="file"
                  class="form-control"
                  accept=".csv"
                  @change="handleFileUpload"
                  :disabled="loading"
                />
              </div>
              <div class="col">
                <select
                  class="form-select"
                  v-model="selectedAgency"
                  :disabled="loading"
                >
                  <option :value="null" disabled>Select Agency</option>
                  <option
                    v-for="agency in agencies"
                    :key="agency.id"
                    :value="agency.id"
                  >
                    {{ agency.name }} ({{ agency.id }})
                  </option>
                </select>
              </div>
            </div>
            <button
              class="btn btn-primary"
              @click="processEmployers"
              :disabled="!csvFile || !selectedAgency || loading"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ loading ? 'Processing...' : 'Process Employers' }}
            </button>

            <!-- Success Messages -->
            <div v-if="processedEmployers.length > 0" class="mt-3">
              <h6>Processed Employers:</h6>
              <ul class="list-group">
                <li
                  v-for="emp in processedEmployers"
                  :key="emp.id"
                  class="list-group-item list-group-item-success"
                >
                  {{ emp.id }} - {{ emp.status }}
                </li>
              </ul>
            </div>

            <!-- Error Messages -->
            <div v-if="errors.length > 0" class="mt-3">
              <h6>Errors:</h6>
              <ul class="list-group">
                <li
                  v-for="(err, idx) in errors"
                  :key="idx"
                  class="list-group-item list-group-item-danger"
                >
                  {{ err.id }} - {{ err.error }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .main-container {
    background-color: #f5f5f5;
    height: 100vh;
  }
</style>

<style>
  /* Global override: increase Bootstrap tooltip width */
  .tooltip-inner {
    max-width: 400px !important;
    white-space: normal !important;
  }
</style>
